/**
 * iter243 — Purchase Order Wizard V2
 *
 * 4-step guided create-PO flow driven by the Excel-based
 * `PURNABRAMHA_DINE_IN_V2` catalog:
 *   Step 1: PO Details (vendor + category + delivery info)
 *   Step 2: Pick Items from Category / Source / Search
 *   Step 3: Review + inline edit qty/rate/gst/discount
 *   Step 4: Preview + Submit + PDF + Email
 *
 * Missing rate = RATE_REQUIRED (Excel blank).  User MUST enter Rate
 * before submitting (SA can override with allow_zero_rate on approve).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, Download, Mail, Search } from 'lucide-react';
import PowizErrorBoundary from '../components/PowizErrorBoundary';

const API = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = ['INR', 'AUD', 'USD', 'GBP', 'AED'];

const currency = (v, code = 'INR') => {
  try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, maximumFractionDigits: 2 }).format(v || 0); }
  catch { return `${code} ${(v || 0).toLocaleString()}`; }
};

const lineTotal = (l) => {
  const q = +(l.quantity || 0), r = +(l.rate || 0), d = +(l.discount || 0), g = +(l.gst_percent || 0);
  const taxable = Math.max(q * r - d, 0);
  return taxable + taxable * g / 100;
};

export function POWizardV2({ open, onClose, call, token, oid, onboarding, onCreated, preset }) {
  const cur = onboarding?.currency_code || 'INR';
  const [step, setStep] = useState(1);
  // Step 1
  const [details, setDetails] = useState({
    vendor: '', vendor_gstin: '', vendor_address: '', vendor_contact: '',
    po_category: '', po_source: '',
    po_category_code: '',  // iter248 — category from Franchise Configuration
    expected_delivery: '', currency_code: cur, gst_split: 'CGST_SGST',
    terms: 'Payment 30 days from invoice. Delivery against a signed GRN.',
    remarks: '',
  });
  // Step 2
  const [catTree, setCatTree] = useState([]);
  const [catItems, setCatItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState({}); // {id: true}
  const [customLines, setCustomLines] = useState([]);
  const [search, setSearch] = useState('');
  // Step 3
  const [reviewLines, setReviewLines] = useState([]);
  // Step 4
  const [preview, setPreview] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', sending: false });
  const [submitting, setSubmitting] = useState(false);

  // iter249b — FRANCHISE MASTER MODE state (declared BEFORE derived
  // constants to avoid TDZ ReferenceError when React renders).
  const [franchisePOMasters, setFranchisePOMasters] = useState([]);
  const [selectedMasterSnapId, setSelectedMasterSnapId] = useState('');

  // iter248 — Preset from Franchise Configuration Category dashboard
  useEffect(() => {
    if (!open || !preset) return;
    // Preload category + lines. Start at step 1 to collect vendor, then jump straight to review (skip Step 2).
    setDetails(d => ({
      ...d,
      po_category_code: preset.po_category_code,
      po_category: preset.po_category_name || preset.po_category_code,
    }));
    const preLines = (preset.lines || []).map(l => ({
      id: l.id, description: l.description, brand: l.brand || '',
      size: l.size || '', item_code: l.item_code || '',
      unit: l.unit || 'NO', quantity: l.quantity || 1,
      rate: l.rate == null ? null : +l.rate,
      discount: l.discount || 0, gst_percent: l.gst_percent || 18,
      hsn_code: l.hsn_code || '',
      rate_status: l.rate_status || 'VALUED',
      commercial_line_id: l.id,
    }));
    setReviewLines(preLines);
    setStep(1);
  }, [open, preset]);

  const step1Valid = details.vendor.trim() && selectedMasterSnapId;
  // hasPreset must be true ONLY when the wizard was launched from a
  // Franchise-Configuration card (with a `preset` prop).  Master-dropdown
  // selections also populate reviewLines but MUST NOT show the preset banner
  // — otherwise the banner dereferences preset.po_category_name and crashes.
  const hasPreset = !!preset && reviewLines.length > 0;

  useEffect(() => {
    if (!open) return;
    // iter249b — in Master mode we skip the legacy catalog fetch
    if (oid) return;
    call('/catalog/v2/categories', {}).then(r => setCatTree(r.tree || [])).catch(e => toast.error(e.message));
  }, [open, call, oid]);

  useEffect(() => {
    // iter249b — skip legacy category-based fetch when in master mode
    if (oid) return;
    if (!details.po_category) { setCatItems([]); return; }
    call('/catalog/v2/list', { category: details.po_category, source_sheet: details.po_source || undefined, search: search || undefined })
      .then(r => setCatItems(r.items || []))
      .catch(e => toast.error(e.message));
  }, [details.po_category, details.po_source, search, call, oid]);

  const currentCatSources = useMemo(() => {
    const c = catTree.find(c => c.category === details.po_category);
    return c?.sources || [];
  }, [catTree, details.po_category]);

  // iter244 — auto-select source when only one exists for the category
  useEffect(() => {
    if (details.po_category && currentCatSources.length === 1 && !details.po_source) {
      setDetails(d => ({ ...d, po_source: currentCatSources[0].source_sheet }));
    }
  }, [details.po_category, currentCatSources]);

  // iter249b — FRANCHISE MASTER MODE
  // Load the franchise's selected PO Masters (snapshots).  When one
  // is selected, the review-lines are pre-populated from the snapshot.
  // Replaces the old PO Category + Source Sheet picker.
  useEffect(() => {
    if (!open || !oid) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masters/franchise/list/${oid}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }).then(r => r.json()).then(r => {
      // Dedupe defensively by id in case the backend returns stale
      // duplicate snapshots for the same worksheet_name.
      const seen = new Set();
      const list = (r.po_snapshots || []).filter(m => {
        if (!m?.id || seen.has(m.id)) return false;
        seen.add(m.id); return true;
      });
      setFranchisePOMasters(list);
    }).catch(() => {});
  }, [open, oid, token]);

  // When preset carries master_snapshot_id, lock the dropdown to it
  useEffect(() => {
    if (preset?.master_snapshot_id) {
      setSelectedMasterSnapId(preset.master_snapshot_id);
    }
  }, [preset]);

  // When the user picks a master → load its snapshot items into reviewLines
  useEffect(() => {
    if (!selectedMasterSnapId) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/masters/franchise/snapshot/${selectedMasterSnapId}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }).then(r => r.json()).then(s => {
      if (cancelled) return;
      if (!s || typeof s !== 'object' || !Array.isArray(s.items)) {
        toast.error('Master snapshot returned no items');
        setReviewLines([]);
        return;
      }
      try {
        const preLines = s.items.map((it, idx) => {
          const spec = (it?.specification || '').toString().trim();
          const item = (it?.item || '').toString().trim();
          const desc = spec ? (item ? `${item}\n${spec}` : spec) : (item || `Item ${idx + 1}`);
          const rate = it?.rate == null ? null : Number(it.rate);
          const qty = it?.quantity == null ? 1 : Number(it.quantity);
          const gst = it?.gst_percent == null ? 18 : Number(it.gst_percent);
          return {
            id: it?.id || `master-${idx}`,
            description: desc,
            brand: '', size: (it?.side || '').toString(),
            item_code: '',
            unit: (it?.unit || 'NO').toString(),
            quantity: isFinite(qty) ? qty : 1,
            rate: rate != null && isFinite(rate) ? rate : null,
            discount: 0,
            gst_percent: isFinite(gst) ? gst : 18,
            hsn_code: '',
            rate_status: (rate && rate > 0) ? 'VALUED' : 'RATE_REQUIRED',
            commercial_line_id: null,
            master_item_id: it?.id,
          };
        });
        setReviewLines(preLines);
        setDetails(d => ({ ...d,
          po_category: s.worksheet_name || '',
          po_category_code: s.worksheet_name || '',
          po_source: `MASTER_${s.worksheet_name || ''}`,
        }));
      } catch (err) {
        console.error('Master snapshot map error', err);
        toast.error('Failed to load master items: ' + (err.message || 'unknown'));
        setReviewLines([]);
      }
    }).catch(err => {
      if (cancelled) return;
      console.error('Snapshot fetch failed', err);
      toast.error('Failed to fetch master snapshot');
    });
    return () => { cancelled = true; };
  }, [selectedMasterSnapId, token]);

  // Auto-suggest vendor when a source_sheet is picked
  useEffect(() => {
    if (details.po_source && catItems.length && !details.vendor) {
      setDetails(d => ({ ...d, vendor: catItems[0].default_supplier || '' }));
    }
  }, [details.po_source, catItems]);

  const totalSelected = Object.values(selectedIds).filter(Boolean).length + customLines.length;

  const toggleSel = (it) => setSelectedIds(p => ({ ...p, [it.id]: !p[it.id] }));
  const selectAll = () => {
    const next = { ...selectedIds };
    catItems.forEach(it => { next[it.id] = true; });
    setSelectedIds(next);
    toast.success(`Selected ${catItems.length} item(s)`);
  };
  const clearAll = () => setSelectedIds({});
  const addCustom = () => setCustomLines(a => [...a, {
    _custom: true, description: '', brand: '', size: '', unit: 'NO',
    quantity: 1, rate: null, discount: 0, gst_percent: 18, hsn_code: '',
    rate_status: 'RATE_REQUIRED',
  }]);
  const setCustomField = (i, k, v) => setCustomLines(a => a.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmCustom = (i) => setCustomLines(a => a.filter((_, idx) => idx !== i));

  // Build the review lines from selection + custom.  Called when
  // moving from Step 2 → Step 3.
  const goToReview = () => {
    if (totalSelected === 0) { toast.error('Pick at least one item'); return; }
    const picked = catItems.filter(it => selectedIds[it.id]).map(it => ({
      id: it.id, description: it.description, brand: it.brand || '',
      size: it.size || '', item_code: it.item_code || '',
      unit: it.unit || 'NO', quantity: it.default_qty || 1,
      rate: it.default_rate == null ? null : +it.default_rate,
      discount: 0, gst_percent: 18, hsn_code: it.hsn_code || '',
      rate_status: it.rate_status, link: it.link || '', remarks: it.remarks || '',
    }));
    setReviewLines([...picked, ...customLines]);
    setStep(3);
  };

  const updateReview = (i, k, v) => setReviewLines(a =>
    a.map((x, idx) => idx === i ? { ...x, [k]: v,
      rate_status: (k === 'rate' && v !== null && v !== '' && +v > 0) ? 'VALUED' : x.rate_status }
      : x));
  const rmReview = (i) => setReviewLines(a => a.filter((_, idx) => idx !== i));

  const missingRateCount = reviewLines.filter(l => l.rate == null || l.rate === '' || +l.rate <= 0).length;
  const grandTotal = reviewLines.reduce((s, l) => s + lineTotal(l), 0);

  const submitPO = async () => {
    if (missingRateCount > 0) {
      toast.error(`${missingRateCount} line(s) still show RATE REQUIRED — enter a rate first`);
      return;
    }
    setSubmitting(true);
    try {
      const additional_lines = reviewLines.map(l => ({
        commercial_line_id: l.commercial_line_id || null,  // iter248
        description: l.description, item_code: l.item_code || '',
        quantity: +l.quantity, unit: l.unit,
        rate: +l.rate, discount: +l.discount || 0,
        gst_percent: +l.gst_percent, hsn_code: l.hsn_code || '',
        billing_source: 'MFPL', classification: 'ASSET',
        brand: l.brand || '', size: l.size || '',
      }));
      const r = await call('/po/create', {
        onboarding_id: oid,
        vendor: details.vendor,
        vendor_gstin: details.vendor_gstin,
        vendor_address: details.vendor_address,
        vendor_contact: details.vendor_contact,
        po_category: details.po_category,
        po_category_code: details.po_category_code || '',  // iter248
        po_source: details.po_source,
        gst_split: details.gst_split,
        expected_delivery: details.expected_delivery,
        currency_code: details.currency_code,
        terms: details.terms, remarks: details.remarks,
        line_ids: [], additional_lines,
      });
      // iter249b — tag the PO with the master_snapshot for dashboard tracking
      if (selectedMasterSnapId && r.po?.id) {
        await fetch(`${API}/api/franchise-onboarding/po/tag-master`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token, po_id: r.po.id,
            master_snapshot_id: selectedMasterSnapId,
            master_worksheet_name: details.po_category,
          }),
        }).catch(() => {});
      }
      toast.success(`PO ${r.po.po_number} created`);
      setPreview({ doc: r.po, lines: r.lines });
      setStep(4);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const downloadPDF = async () => {
    if (!preview) return;
    try {
      const res = await fetch(`${API}/api/franchise-onboarding/po/pdf/${preview.doc.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `PO_${preview.doc.po_number.replace(/\//g, '-')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded and auto-attached to Franchise Documents');
    } catch (e) { toast.error(e.message); }
  };

  const emailPDF = async () => {
    if (!emailForm.to || !emailForm.to.includes('@')) { toast.error('Enter recipient email'); return; }
    setEmailForm(f => ({ ...f, sending: true }));
    try {
      const r = await call(`/po/email/${preview.doc.id}`, {
        to_email: emailForm.to.trim(), cc: (emailForm.cc || '').trim() || null,
      });
      toast.success(`Emailed to ${r.to}${r.cc ? ` (cc ${r.cc})` : ''}`);
      setEmailForm({ to: '', cc: '', sending: false });
    } catch (e) { toast.error(e.message); setEmailForm(f => ({ ...f, sending: false })); }
  };

  const closeAll = () => {
    setStep(1); setDetails({ ...details, remarks: '' });
    setSelectedIds({}); setCustomLines([]); setReviewLines([]);
    setPreview(null); setEmailForm({ to: '', cc: '', sending: false });
    onClose();
  };

  return (
    <PowizErrorBoundary
      onClose={closeAll}
      masterSnapshotId={selectedMasterSnapId}
      onboardingId={oid}
    >
    <Dialog open={open} onOpenChange={closeAll}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto" data-testid="po-wizard-v2">
        <DialogHeader>
          <DialogTitle>Create Purchase Order — Step {step} of 4</DialogTitle>
        </DialogHeader>

        {/* Step ticker */}
        <div className="flex items-center gap-2 text-xs pb-2 border-b">
          {['1. PO Details', '2. Pick Items', '3. Review', '4. Preview'].map((s, idx) => (
            <div key={idx} className={`flex-1 p-2 rounded ${step === idx + 1 ? 'bg-primary text-primary-foreground font-semibold' : step > idx + 1 ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
              {step > idx + 1 && <Check className="w-3 h-3 inline mr-1" />} {s}
            </div>
          ))}
        </div>

        {/* STEP 1 — PO DETAILS */}
        {step === 1 && (
          <div className="space-y-4 pt-3" data-testid="po-step-1">
            {hasPreset && (
              <div className="border border-blue-300 bg-blue-50 rounded p-3 text-xs" data-testid="preset-banner">
                <div className="font-semibold text-blue-800 mb-1">📌 PO Category pre-selected from Franchise Configuration</div>
                <div>
                  <b>{preset?.po_category_name || preset?.po_category_code || ''}</b> — {reviewLines.length} configured line{reviewLines.length === 1 ? '' : 's'} pre-loaded. Enter vendor details below then Skip to Review to edit qty/rate.
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Vendor <span className="text-red-500">*</span></Label>
                <Input value={details.vendor} onChange={e => setDetails(d => ({ ...d, vendor: e.target.value }))} data-testid="w-vendor" />
              </div>
              {/* iter249b — PO MASTER dropdown replaces PO Category + Source Sheet */}
              <div>
                <Label>PO Master <span className="text-red-500">*</span></Label>
                <Select
                  value={selectedMasterSnapId || 'NONE'}
                  onValueChange={v => setSelectedMasterSnapId(v === 'NONE' ? '' : v)}
                  disabled={!!preset?.master_snapshot_id}>
                  <SelectTrigger data-testid="w-po-master"><SelectValue placeholder={franchisePOMasters.length === 0 ? 'No PO Masters selected for this franchise' : 'Select PO Master'} /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="NONE">— Select —</SelectItem>
                    {franchisePOMasters.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.worksheet_name} · {m.row_count} items</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {franchisePOMasters.length === 0 && (
                  <div className="text-[10px] text-amber-700 mt-1">
                    Pick PO Masters in <b>Franchise Setup</b> first.
                  </div>
                )}
                {preset?.master_snapshot_id && (
                  <div className="text-[10px] text-blue-700 mt-1">📌 Locked — launched from card</div>
                )}
              </div>
              <div>
                <Label>Lines pre-loaded</Label>
                <div className="h-9 px-3 border rounded flex items-center bg-muted/40 text-sm">
                  <Badge variant="outline" className="mr-2">{reviewLines.length}</Badge>
                  {selectedMasterSnapId ? 'from selected master' : 'select a master →'}
                </div>
              </div>
              <div>
                <Label>Vendor GSTIN</Label>
                <Input value={details.vendor_gstin} onChange={e => setDetails(d => ({ ...d, vendor_gstin: e.target.value.toUpperCase() }))} />
              </div>
              <div className="col-span-2">
                <Label>Vendor Address</Label>
                <Input value={details.vendor_address} onChange={e => setDetails(d => ({ ...d, vendor_address: e.target.value }))} />
              </div>
              <div>
                <Label>Vendor Contact</Label>
                <Input value={details.vendor_contact} onChange={e => setDetails(d => ({ ...d, vendor_contact: e.target.value }))} />
              </div>
              <div>
                <Label>Expected Delivery</Label>
                <Input type="date" value={details.expected_delivery} onChange={e => setDetails(d => ({ ...d, expected_delivery: e.target.value }))} data-testid="w-eta" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={details.currency_code} onValueChange={v => setDetails(d => ({ ...d, currency_code: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST Split</Label>
                <Select value={details.gst_split} onValueChange={v => setDetails(d => ({ ...d, gst_split: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CGST_SGST">CGST + SGST (intra-state)</SelectItem>
                    <SelectItem value="IGST">IGST (inter-state)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Label>Terms & Conditions</Label>
                <Textarea rows={2} value={details.terms} onChange={e => setDetails(d => ({ ...d, terms: e.target.value }))} />
              </div>
              <div className="col-span-3">
                <Label>Remarks</Label>
                <Textarea rows={2} value={details.remarks} onChange={e => setDetails(d => ({ ...d, remarks: e.target.value }))} />
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — ITEM PICKER */}
        {step === 2 && (
          <div className="pt-3" data-testid="po-step-2">
            <div className="flex items-center gap-2 pb-2">
              <div className="text-xs text-muted-foreground">Category: <b>{details.po_category}</b> · Source: <b>{details.po_source || 'ALL'}</b> · {catItems.length} item(s)</div>
              <div className="flex-1"></div>
              <div className="relative w-64">
                <Search className="absolute left-2 top-2.5 w-3 h-3 text-muted-foreground" />
                <Input className="pl-7 h-8 text-xs" placeholder="Search item..." value={search} onChange={e => setSearch(e.target.value)} data-testid="w-search" />
              </div>
              <Button size="sm" variant="outline" onClick={selectAll} data-testid="w-select-all"><Plus className="w-3 h-3 mr-1" />Select all</Button>
              <Button size="sm" variant="ghost" onClick={clearAll}>Clear</Button>
              <Button size="sm" variant="outline" onClick={addCustom} data-testid="w-add-custom"><Plus className="w-3 h-3 mr-1" />Custom line</Button>
            </div>
            <div className="border rounded max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/40">
                  <tr className="border-b">
                    <th className="p-1 w-8"></th>
                    <th>Item</th><th>Brand</th><th>Size</th><th>U/M</th>
                    <th>Qty</th><th>Rate</th><th>GST%</th><th>HSN</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {catItems.map(it => (
                    <tr key={it.id} className={`border-b ${selectedIds[it.id] ? 'bg-primary/5' : ''}`} data-testid={`w-item-${it.item_code}`}>
                      <td className="p-1 text-center"><input type="checkbox" checked={!!selectedIds[it.id]} onChange={() => toggleSel(it)} /></td>
                      <td>{it.description}</td>
                      <td className="text-xs text-muted-foreground">{it.brand}</td>
                      <td className="text-xs text-muted-foreground">{it.size}</td>
                      <td className="text-center">{it.unit}</td>
                      <td className="text-center">{it.default_qty}</td>
                      <td className="text-right">{it.default_rate == null ? '—' : currency(it.default_rate, cur)}</td>
                      <td className="text-center">{it.gst_percent}%</td>
                      <td className="text-center">{it.hsn_code || '—'}</td>
                      <td className="text-center">
                        <Badge variant="outline" className={`text-[9px] ${it.rate_status === 'RATE_REQUIRED' ? 'text-amber-700 border-amber-500' : 'text-green-700 border-green-500'}`}>
                          {it.rate_status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {catItems.length === 0 && (
                    <tr><td colSpan={10} className="p-4 text-center text-muted-foreground">No items — pick a category above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {customLines.length > 0 && (
              <div className="mt-3 border rounded" data-testid="w-custom-list">
                <div className="bg-muted/40 p-2 text-xs font-semibold">Custom lines ({customLines.length})</div>
                {customLines.map((l, i) => (
                  <div key={i} className="grid grid-cols-8 gap-1 p-1 border-b text-xs">
                    <Input className="h-7 col-span-3" placeholder="Description" value={l.description} onChange={e => setCustomField(i, 'description', e.target.value)} />
                    <Input className="h-7" placeholder="Brand/Size" value={l.brand} onChange={e => setCustomField(i, 'brand', e.target.value)} />
                    <Input className="h-7" type="number" placeholder="Qty" value={l.quantity} onChange={e => setCustomField(i, 'quantity', +e.target.value)} />
                    <Input className="h-7" type="number" placeholder="Rate" value={l.rate ?? ''} onChange={e => setCustomField(i, 'rate', +e.target.value || null)} />
                    <Input className="h-7" type="number" placeholder="GST%" value={l.gst_percent} onChange={e => setCustomField(i, 'gst_percent', +e.target.value)} />
                    <Button size="sm" variant="ghost" onClick={() => rmCustom(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — REVIEW */}
        {step === 3 && (
          <div className="pt-3" data-testid="po-step-3">
            {missingRateCount > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-900 mb-2">
                ⚠ <b>{missingRateCount} line(s) require a Rate</b> before this PO can be submitted.  RATE REQUIRED items (blank in Excel) must be filled in — ₹0 is NOT the same as a missing rate.
              </div>
            )}
            <div className="border rounded max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/40"><tr className="border-b">
                  <th className="p-1">Sr</th><th>Item</th><th>Brand/Size</th><th>U/M</th>
                  <th className="w-16">Qty</th><th className="w-24">Rate</th><th className="w-20">Discount</th>
                  <th className="w-16">GST%</th><th className="w-24 text-right">Total</th><th className="w-8"></th>
                </tr></thead>
                <tbody>
                  {reviewLines.map((l, i) => (
                    <tr key={i} className={`border-b ${(l.rate == null || +l.rate <= 0) ? 'bg-amber-50' : ''}`} data-testid={`w-review-row-${i}`}>
                      <td className="p-1">{i + 1}</td>
                      <td><Input className="h-7 text-xs" value={l.description} onChange={e => updateReview(i, 'description', e.target.value)} /></td>
                      <td className="text-muted-foreground">{[l.brand, l.size].filter(Boolean).join(' · ')}</td>
                      <td>{l.unit}</td>
                      <td><Input className="h-7 text-xs" type="number" value={l.quantity} onChange={e => updateReview(i, 'quantity', +e.target.value)} /></td>
                      <td>
                        <Input className={`h-7 text-xs ${(l.rate == null || +l.rate <= 0) ? 'border-amber-500' : ''}`} type="number" placeholder="RATE REQUIRED" value={l.rate == null ? '' : l.rate} onChange={e => updateReview(i, 'rate', e.target.value === '' ? null : +e.target.value)} data-testid={`w-rate-${i}`} />
                      </td>
                      <td><Input className="h-7 text-xs" type="number" value={l.discount} onChange={e => updateReview(i, 'discount', +e.target.value)} /></td>
                      <td><Input className="h-7 text-xs" type="number" value={l.gst_percent} onChange={e => updateReview(i, 'gst_percent', +e.target.value)} /></td>
                      <td className="text-right pr-1">{currency(lineTotal(l), cur)}</td>
                      <td><Button size="sm" variant="ghost" onClick={() => rmReview(i)}><Trash2 className="w-3 h-3" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 p-2 border rounded bg-muted/20 text-right text-sm">
              Grand Total: <b>{currency(grandTotal, cur)}</b> · Lines: {reviewLines.length}
            </div>
          </div>
        )}

        {/* STEP 4 — PREVIEW */}
        {step === 4 && preview && (
          <div className="pt-3 space-y-3" data-testid="po-step-4">
            <div className="text-sm font-semibold text-primary">
              ✓ PO <span className="font-mono">{preview.doc.po_number}</span> created and saved as DRAFT.
            </div>
            <div className="border rounded max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/20"><tr><th className="p-1">#</th><th>Description</th><th>Qty</th><th>Rate</th><th>GST</th><th className="text-right">Total</th></tr></thead>
                <tbody>{preview.lines.map((l, i) => (
                  <tr key={l.id} className="border-b"><td className="p-1">{i + 1}</td><td>{l.description}</td><td>{l.quantity}</td><td>{currency(l.rate, cur)}</td><td>{l.gst_percent}%</td><td className="text-right font-semibold">{currency(l.final_amount, cur)}</td></tr>
                ))}</tbody>
              </table>
            </div>
            <div className="text-right text-sm">Grand Total: <b>{currency(preview.doc.totals?.final_amount, cur)}</b></div>
            <div className="border rounded p-2 bg-slate-50 space-y-2">
              <div className="text-xs font-semibold">Email this PO to the Vendor</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="vendor@example.com" value={emailForm.to} onChange={e => setEmailForm(f => ({ ...f, to: e.target.value }))} data-testid="w-email-to" />
                <Input placeholder="CC (optional)" value={emailForm.cc} onChange={e => setEmailForm(f => ({ ...f, cc: e.target.value }))} />
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={emailPDF} disabled={emailForm.sending || !emailForm.to} data-testid="w-btn-email"><Mail className="w-3 h-3 mr-1" />{emailForm.sending ? 'Sending…' : 'Email PDF'}</Button>
                <Button size="sm" variant="outline" onClick={downloadPDF} data-testid="w-btn-download"><Download className="w-3 h-3 mr-1" />Download PDF</Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-3 mt-3">
          {step < 4 && <Button variant="outline" onClick={closeAll}>Cancel</Button>}
          {step > 1 && step < 4 && <Button variant="outline" onClick={() => setStep(step - 1)}><ArrowLeft className="w-3 h-3 mr-1" />Back</Button>}
          {step === 1 && (
            <div className="flex items-center gap-3">
              {!step1Valid && (
                <span className="text-[11px] text-amber-700" data-testid="w-next-reason">
                  {!details.vendor.trim() ? 'Enter Vendor to continue.' : 'Select a PO Master to continue.'}
                </span>
              )}
              <Button onClick={() => setStep(3)} disabled={!step1Valid} data-testid="w-next-review">
                Next: Review Items ({reviewLines.length}) <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
          {step === 2 && <Button onClick={goToReview} data-testid="w-next-3">Next: Review ({totalSelected}) <ArrowRight className="w-3 h-3 ml-1" /></Button>}
          {step === 3 && <Button onClick={submitPO} disabled={submitting || missingRateCount > 0} data-testid="w-submit">{submitting ? 'Saving…' : 'Create PO'}</Button>}
          {step === 4 && <Button onClick={() => { onCreated(); closeAll(); }} data-testid="w-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </PowizErrorBoundary>
  );
}

export default POWizardV2;
